import { useEffect, useState, type ReactNode } from 'react';
import {ChevronDown, Menu, Phone, X} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { WhatsAppButton } from './WhatsAppButton';
import logoImage from '../../images/VIP House Movers.png';
import googleBusinessLogo from '../../images/icons/GOOGLEBUSINESSLOGO.png';
import bingLogo from '../../images/icons/BINGLOGO.jpg';
import yellLogoImage from '../../images/icons/Yell logo.png';
import comparemymoveLogo from '../../images/icons/comparemymovelogo.png';
import getamoverLogo from '../../images/icons/GETAMOVERLOGO.jpg';
import thomsonLocalLogo from '../../images/icons/thomsonlocallogo.avif';
import stripeLogo from '../../images/icons/STRIPELOGO.png';
import klarnaLogo from '../../images/icons/KLARNA LOGO.png';

import { areaPages } from '@/lib/areaPages';
import { longDistanceRemovalsPages } from '@/lib/longDistanceRemovalsPages';
import { manAndVanPages } from '@/lib/manAndVanPages';
import { officeRemovalPages } from '@/lib/officeRemovalPages';
import { packingServicesPages } from '@/lib/packingServicesPages';
import { sameDayRemovalsPages } from '@/lib/sameDayRemovalsPages';
import { servicePages } from '@/lib/servicePages';
import { storageServicesPages } from '@/lib/storageServicesPages';
import { studentMovesPages } from '@/lib/studentMovesPages';

type NavItem = {
  label: string;
  to: string;
  children?: NavItem[];
};

type SiteShellProps = {
  children: ReactNode;
  activePath: string;
  fixedNav?: boolean;
  serviceMenuPath?: string;
  areaMenuPath?: string;
};

const navItems: NavItem[] = [
  { label: 'About Us', to: '/about' },
  {
    label: 'Services',
    to: '/services',
    children: servicePages.map((service) => {
      if (service.path === '/services/office-removals') {
        return {
          label: service.navLabel,
          to: service.path,
          children: officeRemovalPages.map((office) => ({
            label: office.navLabel,
            to: office.path,
          })),
        };
      }
      if (service.path === '/services/man-and-van') {
        return {
          label: service.navLabel,
          to: service.path,
          children: manAndVanPages.map((mav) => ({
            label: mav.navLabel,
            to: mav.path,
          })),
        };
      }
      if (service.path === '/services/packing-services') {
        return {
          label: service.navLabel,
          to: service.path,
          children: packingServicesPages.map((pack) => ({
            label: pack.navLabel,
            to: pack.path,
          })),
        };
      }
      if (service.path === '/services/storage-solutions') {
        return {
          label: service.navLabel,
          to: service.path,
          children: storageServicesPages.map((storage) => ({
            label: storage.navLabel,
            to: storage.path,
          })),
        };
      }
      if (service.path === '/services/same-day-moves') {
        return {
          label: service.navLabel,
          to: service.path,
          children: sameDayRemovalsPages.map((sameDay) => ({
            label: sameDay.navLabel,
            to: sameDay.path,
          })),
        };
      }
      if (service.path === '/services/student-moves') {
        return {
          label: service.navLabel,
          to: service.path,
          children: studentMovesPages.map((student) => ({
            label: student.navLabel,
            to: student.path,
          })),
        };
      }
      if (service.path === '/services/long-distance-removals') {
        return {
          label: service.navLabel,
          to: service.path,
          children: longDistanceRemovalsPages.map((ld) => ({
            label: ld.navLabel,
            to: ld.path,
          })),
        };
      }
      return {
        label: service.navLabel,
        to: service.path,
      };
    }),
  },
  {
    label: 'Areas We Cover',
    to: '/areas-we-cover',
    children: areaPages.map((area) => ({
      label: area.navLabel,
      to: area.path,
    })),
  },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Packing Supplies', to: '/packing-supplies' },
  { label: 'Reviews', to: '/reviews' },
  { label: 'FAQs', to: '/faq' },
  { label: 'Blog', to: '/blog' },
  { label: 'Contact', to: '/contact' },
];

const navLinkClass = 'hover:text-[#123499] transition-colors relative after:absolute after:-bottom-1 after:left-0 after:w-full after:h-[2px] after:bg-[#123499] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300';
const activeNavLinkClass = 'text-[#123499] relative after:absolute after:-bottom-1 after:left-0 after:w-full after:h-[2px] after:bg-[#123499] after:scale-x-100';

export function SiteShell({ children, activePath, fixedNav = false, serviceMenuPath, areaMenuPath }: SiteShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [desktopExpanded, setDesktopExpanded] = useState<Record<string, boolean>>({});
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setExpandedGroups({});
    setDesktopExpanded({});
  }, [location.pathname]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDesktop = (key: string) => {
    setDesktopExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const navClassName = fixedNav
    ? 'fixed left-0 right-0 z-50 bg-surface/95 backdrop-blur-md border-b border-gray-200'
    : 'sticky z-50 bg-surface/95 backdrop-blur-md border-b border-gray-200';

  return (
    <div className="min-h-screen bg-gray-50 selection:bg-[#0F4A4E] selection:text-white">
      <nav
        className={navClassName}
        style={fixedNav ? { top: 0 } : undefined}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 md:h-24 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2" aria-label="VIP House Movers - Home">
              {/* Logo always visible */}
              <img
                src={logoImage}
                alt="VIP House Movers Logo"
                className="h-16 md:h-20 w-auto object-contain drop-shadow-sm hover:drop-shadow-md transition-all hover:scale-105"
              />
            </Link>
            <div className="hidden xl:block h-6 w-px bg-gray-300 mx-4" />
            <div className="hidden xl:flex items-center gap-2 text-gray-600 group hover:text-[#0F4A4E] transition-colors">
              <Phone className="w-4 h-4 text-[#0F4A4E]" />
              <span className="font-semibold px-2 py-1 rounded-full bg-[#E7F2F2] border border-[#B7D1D2] group-hover:bg-[#D6E9EA] transition-colors">
                0121 752 7107
              </span>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-x-4 xl:gap-x-6 text-sm font-semibold text-gray-700 whitespace-nowrap">
            {navItems.map((item) => {
              const itemDescendantActive = item.children?.some((child) =>
                child.to === activePath ||
                child.to === serviceMenuPath ||
                child.to === areaMenuPath ||
                child.children?.some((gc) => gc.to === activePath || gc.to === serviceMenuPath || gc.to === areaMenuPath)
              );
              const isActive = activePath === item.to
                || serviceMenuPath === item.to
                || areaMenuPath === item.to
                || itemDescendantActive;

              if (item.children?.length) {
                return (
                  <div key={item.to} className="group relative">
                    <Link
                      to={item.to}
                      className={`${isActive ? activeNavLinkClass : navLinkClass} inline-flex items-center gap-2`}
                    >
                      {item.label}
                      <span className="text-xs transition-transform duration-200 group-hover:rotate-180">▾</span>
                    </Link>
                    <div className="invisible absolute left-0 top-full z-50 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                      <div className="grid w-80 gap-2 rounded-2xl border border-gray-200 bg-white p-3 shadow-2xl shadow-gray-900/10 max-h-[75vh] overflow-y-auto overflow-x-visible">
                        {item.children.map((child) => {
                          const grandActive = child.children?.some((gc) => gc.to === activePath || gc.to === serviceMenuPath || gc.to === areaMenuPath);
                          const childActive = activePath === child.to || serviceMenuPath === child.to || areaMenuPath === child.to || grandActive;

                          if (child.children?.length) {
                            const subKey = `desktop::${item.to}::${child.to}`;
                            const subExpanded = desktopExpanded[subKey] ?? false;
                            return (
                              <div key={child.to} className="rounded-xl border border-gray-100 bg-white">
                                <div className="flex items-stretch">
                                  <Link
                                    to={child.to}
                                    className={`flex-1 px-4 py-3 text-sm font-semibold rounded-l-xl transition-colors ${childActive ? 'bg-[#E7F2F2] text-[#0F4A4E]' : 'text-gray-700 hover:bg-gray-50 hover:text-[#0F4A4E]'}`}
                                  >
                                    {child.label}
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleDesktop(subKey);
                                    }}
                                    aria-expanded={subExpanded}
                                    aria-label={`${subExpanded ? 'Collapse' : 'Expand'} ${child.label} locations`}
                                    className="px-3 flex items-center justify-center text-gray-500 hover:text-[#0F4A4E] border-l border-gray-100 rounded-r-xl"
                                  >
                                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${subExpanded ? 'rotate-180' : ''}`} />
                                  </button>
                                </div>
                                <div className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${subExpanded ? 'max-h-[60vh]' : 'max-h-0'}`}>
                                  <div className="px-2 pb-2 pt-1 grid gap-1">
                                    <div className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0F4A4E]">
                                      {child.label} in
                                    </div>
                                    {child.children.map((grand) => {
                                      const gActive = activePath === grand.to || serviceMenuPath === grand.to || areaMenuPath === grand.to;
                                      return (
                                        <Link
                                          key={grand.to}
                                          to={grand.to}
                                          className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${gActive ? 'bg-[#E7F2F2] text-[#0F4A4E]' : 'text-gray-700 hover:bg-gray-50 hover:text-[#0F4A4E]'}`}
                                        >
                                          {grand.label}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <Link
                              key={child.to}
                              to={child.to}
                              className={`rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${childActive ? 'bg-[#E7F2F2] text-[#0F4A4E]' : 'text-gray-700 hover:bg-gray-50 hover:text-[#0F4A4E]'}`}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={isActive ? activeNavLinkClass : navLinkClass}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              to="/quote"
              className="bg-[#0F4A4E] hover:bg-[#0C3D41] text-white px-6 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all font-bold tracking-wide transform hover:-translate-y-[1px]"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-panel"
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg border border-gray-200 bg-white text-gray-800 hover:text-[#0F4A4E] hover:border-[#B7D1D2] shadow-sm transition-colors"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile dropdown panel */}
        <div
          id="mobile-nav-panel"
          className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out border-t border-gray-200 bg-white ${mobileOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}
        >
          <div className="max-h-[80vh] overflow-y-auto px-4 py-4 space-y-1">
            {navItems.map((item) => {
              const itemDescendantActive = item.children?.some((child) =>
                child.to === activePath ||
                child.to === serviceMenuPath ||
                child.to === areaMenuPath ||
                child.children?.some((gc) => gc.to === activePath || gc.to === serviceMenuPath || gc.to === areaMenuPath)
              );
              const isActive = activePath === item.to
                || serviceMenuPath === item.to
                || areaMenuPath === item.to
                || itemDescendantActive;

              if (item.children?.length) {
                const expanded = expandedGroups[item.to] ?? false;
                return (
                  <div key={item.to} className="rounded-xl border border-gray-100 bg-gray-50/60">
                    <div className="flex items-stretch">
                      <Link
                        to={item.to}
                        className={`flex-1 px-4 py-3 font-semibold rounded-l-xl ${isActive ? 'text-[#0F4A4E]' : 'text-gray-800 hover:text-[#0F4A4E]'}`}
                      >
                        {item.label}
                      </Link>
                      <button
                        type="button"
                        onClick={() => toggleGroup(item.to)}
                        aria-expanded={expanded}
                        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.label} submenu`}
                        className="px-4 flex items-center justify-center text-gray-600 hover:text-[#0F4A4E] border-l border-gray-200 rounded-r-xl"
                      >
                        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    <div className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${expanded ? 'max-h-[2000px]' : 'max-h-0'}`}>
                      <div className="px-2 pb-2 pt-1 grid gap-1">
                        {item.children.map((child) => {
                          const grandActive = child.children?.some((gc) => gc.to === activePath || gc.to === serviceMenuPath || gc.to === areaMenuPath);
                          const childActive = activePath === child.to || serviceMenuPath === child.to || areaMenuPath === child.to || grandActive;

                          if (child.children?.length) {
                            const subKey = `${item.to}::${child.to}`;
                            const subExpanded = expandedGroups[subKey] ?? false;
                            return (
                              <div key={child.to} className="rounded-lg border border-gray-100 bg-white">
                                <div className="flex items-stretch">
                                  <Link
                                    to={child.to}
                                    className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-l-lg ${childActive ? 'text-[#0F4A4E]' : 'text-gray-700 hover:text-[#0F4A4E]'}`}
                                  >
                                    {child.label}
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={() => toggleGroup(subKey)}
                                    aria-expanded={subExpanded}
                                    aria-label={`${subExpanded ? 'Collapse' : 'Expand'} ${child.label} submenu`}
                                    className="px-3 flex items-center justify-center text-gray-500 hover:text-[#D4AF37] border-l border-gray-100 rounded-r-lg"
                                  >
                                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${subExpanded ? 'rotate-180' : ''}`} />
                                  </button>
                                </div>
                                <div className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${subExpanded ? 'max-h-[1500px]' : 'max-h-0'}`}>
                                  <div className="px-2 pb-2 pt-1 grid gap-1">
                                    {child.children.map((grand) => {
                                      const gActive = activePath === grand.to || serviceMenuPath === grand.to || areaMenuPath === grand.to;
                                      return (
                                        <Link
                                          key={grand.to}
                                          to={grand.to}
                                          className={`block rounded-md px-3 py-2 text-sm transition-colors ${gActive ? 'bg-[#E7F2F2] text-[#0F4A4E] font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-[#0F4A4E]'}`}
                                        >
                                          {grand.label}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <Link
                              key={child.to}
                              to={child.to}
                              className={`block rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${childActive ? 'bg-[#E7F2F2] text-[#0F4A4E]' : 'text-gray-700 hover:bg-white hover:text-[#0F4A4E]'}`}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`block rounded-xl px-4 py-3 font-semibold border border-transparent transition-colors ${isActive ? 'bg-[#E7F2F2] text-[#0F4A4E] border-[#B7D1D2]' : 'text-gray-800 hover:bg-gray-50 hover:text-[#0F4A4E]'}`}
                >
                  {item.label}
                </Link>
              );
            })}

            <div className="pt-3 flex flex-col gap-3">
              <Link
                to="/quote"
                className="w-full text-center bg-[#0F4A4E] hover:bg-[#0C3D41] text-white px-6 py-3 rounded-lg shadow-sm hover:shadow-md transition-all font-bold tracking-wide"
              >
                Get Quote
              </Link>
              <a
                href="tel:01217527107"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-[#B7D1D2] bg-[#E7F2F2] text-gray-800 font-semibold hover:bg-[#D6E9EA] transition-colors"
              >
                <Phone className="w-4 h-4 text-[#0F4A4E]" />
                0121 752 7107
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer compensates for the banner height when visible. We animate
          max-height on a single element (not a sibling chain) with the same
          easing as the banner so the page content reflows smoothly. */}


      <main id="main-content">{children}</main>

      <footer className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-[minmax(320px,380px)_1fr]">
            <div className="space-y-10">
              <div className="flex items-center gap-4">
                <Link to="/" className="flex items-center gap-4">
                  <img
                    src={logoImage}
                    alt="VIP House Movers Logo"
                    className="h-20 w-auto object-contain"
                  />
                  <div>
                    <h3 className="text-3xl font-bold text-slate-950">VIP House Movers</h3>
                    <p className="text-slate-600 uppercase tracking-[0.3em] text-sm font-semibold mt-1">
                      Premium Removal Services
                    </p>
                  </div>
                </Link>
              </div>

              <p className="max-w-xl text-slate-600 text-lg leading-relaxed">
                We combine trusted partners, flexible payment options, and strong security guarantees so every move feels safe, simple and well supported.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-[1.5rem] bg-white p-6 border border-slate-200 shadow-sm">
                  <h4 className="text-base font-semibold text-slate-900 mb-2">Contact</h4>
                  <p className="text-slate-600 leading-relaxed">
                    <span className="block font-medium">Phone</span>
                    <a href="tel:01217527107" className="text-slate-700 hover:text-[#0F4A4E] transition-colors">0121 752 7107</a>
                  </p>
                </div>
                <div className="rounded-[1.5rem] bg-white p-6 border border-slate-200 shadow-sm">
                  <h4 className="text-base font-semibold text-slate-900 mb-2">Head Office</h4>
                  <p className="text-slate-600 leading-relaxed">
                    Self Storage, Wolverhampton<br />
                    Vulcan Rd, Wolverhampton<br />
                    Bilston WV14 7LF
                  </p>
                </div>
                <div className="rounded-[1.5rem] bg-white p-6 border border-slate-200 shadow-sm sm:col-span-2">
                  <h4 className="text-base font-semibold text-slate-900 mb-2">Email</h4>
                  <a href="mailto:info@viphousemovers.com" className="text-slate-700 hover:text-[#0F4A4E] transition-colors">info@viphousemovers.com</a>
                  <p className="text-slate-500 text-sm mt-3">Available for quotes, customer support, and booking enquiries.</p>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 shadow-xl min-h-[340px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2425.408996126684!2d-2.063764422986951!3d52.562214533102754!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48709bb15aeadb19%3A0x9f5f945183218f72!2sVIP%20House%20Movers!5e0!3m2!1sen!2suk!4v1776975400021!5m2!1sen!2suk"
                className="w-full h-full border-0 grayscale-[15%] contrast-125"
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="VIP House Movers Map Location"
              />
            </div>
          </div>

          <div className="mt-16 space-y-10">
            <section className="rounded-[1.75rem] bg-white border border-slate-200 p-8 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-sm uppercase tracking-[0.3em] text-slate-500 font-semibold">Partners</p>
                  <h2 className="mt-3 text-2xl font-bold text-slate-950">Our partner network</h2>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                <a href="https://www.comparemymove.com/directory/vip-house-movers-1" target="_blank" rel="noreferrer" className="group flex h-24 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-[#0F4A4E] hover:bg-white">
                  <img src={comparemymoveLogo} alt="CompareMyMove" className="h-12 w-auto object-contain" />
                </a>
                <a href="https://homemove.com/" target="_blank" rel="noreferrer" className="group flex h-24 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-[#0F4A4E] hover:bg-white">
                  <img src="https://homemove.com/images/web/homemove-logo-384.png" alt="HomeMove" className="h-12 w-auto object-contain" />
                </a>
                <a href="https://www.getamover.co.uk/movers/nK4Lx/vip-house-movers/" target="_blank" rel="noreferrer" className="group flex h-24 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-[#0F4A4E] hover:bg-white">
                  <img src={getamoverLogo} alt="Getamover" className="h-12 w-auto object-contain" />
                </a>
                <a href="https://www.google.com/business/" target="_blank" rel="noreferrer" className="group flex h-24 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-[#0F4A4E] hover:bg-white">
                  <img src={googleBusinessLogo} alt="Google Business" className="h-12 w-auto object-contain" />
                </a>
                <a href="https://www.bingplaces.com/" target="_blank" rel="noreferrer" className="group flex h-24 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-[#0F4A4E] hover:bg-white">
                  <img src={bingLogo} alt="Bing Places" className="h-12 w-auto object-contain" />
                </a>
                <a href="https://www.yell.com/biz/vip-house-movers-ltd-bilston-11029316/" target="_blank" rel="noreferrer" className="group flex h-24 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-[#0F4A4E] hover:bg-white">
                  <img src={yellLogoImage} alt="Yell" className="h-12 w-auto object-contain" />
                </a>
                <a href="https://www.thomsonlocal.com/search/removals/wolverhampton/vip-house-movers/3503525/01217527107" target="_blank" rel="noreferrer" className="group flex h-24 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-[#0F4A4E] hover:bg-white">
                  <img src={thomsonLocalLogo} alt="Thomson Local" className="h-12 w-auto object-contain" />
                </a>
              </div>
            </section>

            <div className="grid gap-8 xl:grid-cols-[1.3fr_auto]">
              <section className="rounded-[1.75rem] bg-white border border-slate-200 p-8 shadow-sm">
                <p className="text-sm uppercase tracking-[0.3em] text-slate-500 font-semibold">Payment methods</p>
                <h3 className="mt-3 text-2xl font-bold text-slate-950">Secure checkout options</h3>
                <div className="mt-8 grid grid-cols-3 gap-3">
                  <a href="https://www.blinkpayment.co.uk/" target="_blank" rel="noreferrer" className="group flex h-24 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-[#0F4A4E] hover:bg-white">
                    <img src="https://www.blinkpayment.co.uk/wp-content/uploads/2026/03/Logotype.svg" alt="Blink Payment" className="h-12 w-auto object-contain" />
                  </a>
                  <a href="https://stripe.com/gb" target="_blank" rel="noreferrer" className="group flex h-24 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-[#0F4A4E] hover:bg-white">
                    <img src={stripeLogo} alt="Stripe" className="h-12 w-auto object-contain" />
                  </a>
                  <a href="https://www.klarna.com/uk/" target="_blank" rel="noreferrer" className="group flex h-24 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-4 transition hover:border-[#0F4A4E] hover:bg-white">
                    <img src={klarnaLogo} alt="Klarna" className="h-12 w-auto object-contain" />
                  </a>
                </div>
              </section>

              <section className="rounded-[1.75rem] bg-white border border-slate-200 p-8 shadow-sm">
                <p className="text-sm uppercase tracking-[0.3em] text-slate-500 font-semibold">Social icons</p>
                <h3 className="mt-3 text-2xl font-bold text-slate-950">Follow VIP House Movers</h3>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a href="https://www.facebook.com/viphousemovers" target="_blank" rel="noreferrer" className="flex h-14 w-14 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 transition hover:border-[#0F4A4E]">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg" alt="Facebook" className="h-8 w-8 object-contain" />
                  </a>
                  <a href="https://www.instagram.com/viphousemovers" target="_blank" rel="noreferrer" className="flex h-14 w-14 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 transition hover:border-[#0F4A4E]">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png" alt="Instagram" className="h-8 w-8 object-contain" />
                  </a>
                  <a href="https://www.tiktok.com/@viphousemovers" target="_blank" rel="noreferrer" className="flex h-14 w-14 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 transition hover:border-[#0F4A4E]">
                    <img src="https://upload.wikimedia.org/wikipedia/en/a/a9/TikTok_logo.svg" alt="TikTok" className="h-8 w-8 object-contain" />
                  </a>
                  <a href="https://x.com/viphousemovers" target="_blank" rel="noreferrer" className="flex h-14 w-14 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 transition hover:border-[#0F4A4E]">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/2/2f/X_Logo.svg" alt="X" className="h-8 w-8 object-contain" />
                  </a>
                  <a href="https://www.linkedin.com/company/vip-house-movers" target="_blank" rel="noreferrer" className="flex h-14 w-14 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 transition hover:border-[#0F4A4E]">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png" alt="LinkedIn" className="h-8 w-8 object-contain" />
                  </a>
                  <a href="https://wa.me/447721551617?utm_source=chatgpt.com" target="_blank" rel="noreferrer" className="flex h-14 w-14 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 transition hover:border-[#0F4A4E]">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" className="h-8 w-8 object-contain" />
                  </a>
                </div>
              </section>
            </div>

            <section className="rounded-[1.75rem] bg-white border border-slate-200 p-8 shadow-sm">
              <p className="text-sm uppercase tracking-[0.3em] text-slate-500 font-semibold">Trust badges</p>
              <h3 className="mt-3 text-2xl font-bold text-slate-950">Security you can see</h3>

              <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <a href="https://trustlock.co" target="_blank" rel="noreferrer" className="flex items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
                  <img src="https://trustlock.co/wp-content/uploads/2019/01/guaranteed-safe-checkout-15.png" alt="Guarantee badge" className="h-12 w-auto object-contain" />
                </a>
                <a href="https://trustlock.co" target="_blank" rel="noreferrer" className="flex items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
                  <img src="https://trustlock.co/wp-content/uploads/2019/01/ssl-secure-checkout-trust-badge-seal.png" alt="SSL secure checkout badge" className="h-12 w-auto object-contain" />
                </a>
                <a href="https://trustlock.co" target="_blank" rel="noreferrer" className="flex items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
                  <img src="https://trustlock.co/wp-content/uploads/2019/01/trust-seal-aes-256-bit-ssl.png" alt="AES 256-bit SSL badge" className="h-12 w-auto object-contain" />
                </a>
                <a href="https://trustlock.co" target="_blank" rel="noreferrer" className="flex items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
                  <img src="https://trustlock.co/wp-content/uploads/2019/01/free-credot-card-logos-3.png" alt="Payment badge" className="h-12 w-auto object-contain" />
                </a>
              </div>
            </section>
          </div>

          <div className="mt-16 border-t border-slate-200 pt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} VIP House Movers. All rights reserved.</p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/sitemap" className="hover:text-[#0F4A4E] transition-colors">Sitemap</Link>
              <Link to="/contact" className="hover:text-[#0F4A4E] transition-colors">Contact</Link>
              <a href="mailto:info@viphousemovers.com" className="hover:text-[#0F4A4E] transition-colors">info@viphousemovers.com</a>
            </div>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
}
